import type {
  MapArea,
  MapAreaPoint,
  MapFeatureLine,
  MapFeatureRect,
  MapFeatures,
  MapGeoreference,
  MapSpecialZone,
  MapStartPoint,
  ParsedRoomArea,
  RawGoalWithHeading,
  RawMapBox,
  RawMapConfig,
  RawMapFunction,
  RawMapPoint,
  RawMapWork,
  RawRoomArea,
  RawSweepAreaAttr,
  SweepAreaSuctionMode,
  ValidRoomArea,
  WorldBounds,
} from '../types/MapArea.types';

export const EMPTY_MAP_FEATURES: MapFeatures = {
  startPoints: [],
  specialZones: [],
  noGoAreas: [],
  noGoLines: [],
  glassLines: [],
};

const CENTER_PADDING_PERCENT = 20;
const CENTER_SPAN_PERCENT = 100 - CENTER_PADDING_PERCENT * 2;

const isValidRoomArea = (area: RawRoomArea): area is ValidRoomArea =>
  Boolean(area.id && area.name && area.min && area.max);

const isValidPoint = (value: unknown): value is RawMapPoint =>
  Boolean(value) && typeof (value as RawMapPoint).x === 'number' && typeof (value as RawMapPoint).y === 'number';

const safeParse = <T>(value: string | null | undefined): T | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

/**
 * A RoomArea's outline lives in its `attr` field, itself a JSON-encoded string of the shape
 * `{"vertex":[{x,y},...]}` — `min`/`max` alone only give an axis-aligned bounding box. Falls back
 * to that bounding-box rectangle if `attr` is missing or doesn't parse into a valid polygon.
 */
const parseVertices = (area: ValidRoomArea): RawMapPoint[] => {
  if (area.attr) {
    try {
      const parsedAttr = JSON.parse(area.attr) as { vertex?: unknown };
      if (Array.isArray(parsedAttr.vertex) && parsedAttr.vertex.length >= 3 && parsedAttr.vertex.every(isValidPoint)) {
        return parsedAttr.vertex as RawMapPoint[];
      }
    } catch {
      // fall through to the min/max rectangle below
    }
  }

  return [
    { x: area.min.x, y: area.min.y },
    { x: area.max.x, y: area.min.y },
    { x: area.max.x, y: area.max.y },
    { x: area.min.x, y: area.max.y },
  ];
};

/**
 * Reads `config`'s Resolution; null if missing/invalid, so callers fall back to the heuristic in
 * createMmToPercentConverter. OriginPose/TransformPose are ignored — the backend renderer skips them too.
 */
export const parseMapConfig = (config: string | null | undefined): MapGeoreference | null => {
  if (!config) {
    return null;
  }

  let parsed: RawMapConfig;
  try {
    parsed = JSON.parse(config);
  } catch {
    return null;
  }

  if (typeof parsed.Resolution !== 'number' || !(parsed.Resolution > 0)) {
    return null;
  }

  return { resolutionMm: parsed.Resolution };
};

const getWorldBounds = (points: RawMapPoint[]): WorldBounds => {
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
};

const polygonCenter = (polygon: MapAreaPoint[]): MapAreaPoint => ({
  x: polygon.reduce((sum, point) => sum + point.x, 0) / polygon.length,
  y: polygon.reduce((sum, point) => sum + point.y, 0) / polygon.length,
});

/**
 * Angle (deg) of a polygon's longest edge, for aligning a label to it. The mm→pixel transform is
 * a uniform scale with a flipped Y, so the mm-space angle is the screen angle once Y is negated.
 * Folded into (-90, 90] so text drawn at this angle always stays upright.
 */
const longestEdgeAngleDeg = (vertices: RawMapPoint[]): number => {
  let angle = 0;
  let longest = -1;
  for (let i = 0; i < vertices.length; i += 1) {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    const lengthSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
    if (lengthSq > longest) {
      longest = lengthSq;
      angle = Math.atan2(-(b.y - a.y), b.x - a.x) * (180 / Math.PI);
    }
  }
  if (angle > 90) {
    angle -= 180;
  }
  if (angle <= -90) {
    angle += 180;
  }
  // atan2 can yield -0 for a horizontal/vertical edge; normalize so callers never see it.
  return angle === 0 ? 0 : angle;
};

interface MmToPercentConverterOptions {
  bounds: WorldBounds;
  imageWidthPx: number | undefined;
  imageHeightPx: number | undefined;
  georeference: MapGeoreference | null;
}

/**
 * Converts a point in the robot's coordinate space (mm) to percentage-of-image coordinates.
 * 1. Georeferenced (preferred): config Resolution + known image size — mirrors the map backend's
 *    renderer exactly (px = mm / Resolution, Y flipped, no origin term). Needs map.image at its
 *    real width/height.
 * 2. Aspect-correct fallback: image size known but no config — uniform scale, centred.
 * 3. Padded fallback: nothing known — pad each axis into a centred 20-80% band.
 */
const createMmToPercentConverter = ({
  bounds,
  imageWidthPx,
  imageHeightPx,
  georeference,
}: MmToPercentConverterOptions): ((point: RawMapPoint) => MapAreaPoint) => {
  const worldWidth = bounds.maxX - bounds.minX || 1;
  const worldHeight = bounds.maxY - bounds.minY || 1;

  if (georeference && imageWidthPx && imageHeightPx) {
    // `(mm * 100) / (resolution * imagePx)` rather than chained divides — same result, but
    // exact for the round inputs these maps use (e.g. 5000mm / 50 / 1000px * 100 drifts off 10).
    const xDivisor = georeference.resolutionMm * imageWidthPx;
    const yDivisor = georeference.resolutionMm * imageHeightPx;
    return point => ({
      x: (point.x * 100) / xDivisor,
      y: 100 - (point.y * 100) / yDivisor,
    });
  }

  if (imageWidthPx && imageHeightPx) {
    const scale = Math.min(imageWidthPx / worldWidth, imageHeightPx / worldHeight);
    const offsetX = (imageWidthPx - worldWidth * scale) / 2;
    const offsetY = (imageHeightPx - worldHeight * scale) / 2;

    return point => ({
      x: ((offsetX + (point.x - bounds.minX) * scale) / imageWidthPx) * 100,
      // Map coordinates are Y-up; screen/image coordinates are Y-down, so the axis is flipped.
      y: ((offsetY + (bounds.maxY - point.y) * scale) / imageHeightPx) * 100,
    });
  }

  return point => ({
    x: CENTER_PADDING_PERCENT + ((point.x - bounds.minX) / worldWidth) * CENTER_SPAN_PERCENT,
    y: CENTER_PADDING_PERCENT + ((bounds.maxY - point.y) / worldHeight) * CENTER_SPAN_PERCENT,
  });
};

/**
 * Areas for a CV50 map, parsed from its RoomArea list (`work`, inline JSON from the robot).
 * Outline/position are converted from mm to percentage-of-image via createMmToPercentConverter.
 */
export const parseMapAreasFromWork = (
  work: string | null | undefined,
  imageWidthPx?: number | null,
  imageHeightPx?: number | null,
  mapConfig?: string | null
): MapArea[] => {
  if (!work) {
    return [];
  }

  let parsed: RawMapWork;
  try {
    parsed = JSON.parse(work);
  } catch {
    return [];
  }

  const roomAreas = (parsed.RoomArea ?? []).filter(isValidRoomArea);
  if (!roomAreas.length) {
    return [];
  }

  const parsedRoomAreas: ParsedRoomArea[] = roomAreas.map(area => ({
    id: area.id,
    name: area.name,
    vertices: parseVertices(area),
  }));

  const bounds = getWorldBounds(parsedRoomAreas.flatMap(area => area.vertices));
  const toPercent = createMmToPercentConverter({
    bounds,
    imageWidthPx: imageWidthPx ?? undefined,
    imageHeightPx: imageHeightPx ?? undefined,
    georeference: parseMapConfig(mapConfig),
  });

  return parsedRoomAreas.map(({ id, name, vertices }) => {
    const polygon = vertices.map(toPercent);
    const { x, y } = polygonCenter(polygon);

    return { id, name, position: { x, y }, polygon };
  });
};

const rectVertices = (box: RawMapBox): RawMapPoint[] | null => {
  if (!box.min || !box.max) {
    return null;
  }
  return [
    { x: box.min.x, y: box.min.y },
    { x: box.max.x, y: box.min.y },
    { x: box.max.x, y: box.max.y },
    { x: box.min.x, y: box.max.y },
  ];
};

/**
 * A box's `attr` may carry a rotated 4-point outline (e.g. a sloped SweepArea or an angled
 * ForbiddenArea) — falls back to the axis-aligned min/max rectangle if `attr` is missing/invalid.
 */
const boxOutlineVertices = (box: RawMapBox): RawMapPoint[] | null => {
  if (box.attr) {
    try {
      const parsedAttr = JSON.parse(box.attr) as RawSweepAreaAttr;
      if (Array.isArray(parsedAttr.vertex) && parsedAttr.vertex.length >= 3 && parsedAttr.vertex.every(isValidPoint)) {
        return parsedAttr.vertex;
      }
    } catch {
      // fall through to the min/max rectangle
    }
  }
  return rectVertices(box);
};

const parseSweepAreaAttr = (attr: string | undefined): { times: number; suctionMode: SweepAreaSuctionMode } => {
  let times = 1;
  let suctionMode: SweepAreaSuctionMode = 'normal';
  if (attr) {
    try {
      const parsed = JSON.parse(attr) as RawSweepAreaAttr;
      if (parsed.times === 2) {
        times = 2;
      }
      const mode = typeof parsed.mode === 'string' ? parsed.mode.toLowerCase() : undefined;
      if (mode === 'eco') {
        suctionMode = 'eco';
      } else if (mode === 'boost') {
        suctionMode = 'boost';
      }
    } catch {
      // keep the defaults
    }
  }
  return { times, suctionMode };
};

const toLine = (box: RawMapBox, toPercent: (point: RawMapPoint) => MapAreaPoint): MapFeatureLine | null => {
  if (!box.min || !box.max) {
    return null;
  }
  return { from: toPercent(box.min), to: toPercent(box.max) };
};

const toRect = (box: RawMapBox, toPercent: (point: RawMapPoint) => MapAreaPoint): MapFeatureRect | null => {
  const vertices = boxOutlineVertices(box);
  return vertices ? { polygon: vertices.map(toPercent) } : null;
};

const isNonEmpty = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

const parseStartPoints = (
  goals: RawGoalWithHeading[],
  toPercent: (point: RawMapPoint) => MapAreaPoint
): MapStartPoint[] =>
  goals
    .map((goal, index): MapStartPoint | null => {
      if (!goal.pose || !isValidPoint(goal.pose)) {
        return null;
      }
      return {
        id: goal.id ?? `${goal.name ?? 'goal'}-${index}`,
        isChargingDock: goal.type === 2,
        position: toPercent(goal.pose),
      };
    })
    .filter(isNonEmpty);

const parseSpecialZones = (
  sweepAreas: RawMapBox[],
  toPercent: (point: RawMapPoint) => MapAreaPoint
): MapSpecialZone[] =>
  sweepAreas
    .map((box, index): MapSpecialZone | null => {
      const vertices = boxOutlineVertices(box);
      if (!vertices) {
        return null;
      }
      const polygon = vertices.map(toPercent);
      const { times, suctionMode } = parseSweepAreaAttr(box.attr);
      return {
        id: `sweep-${index}`,
        polygon,
        position: polygonCenter(polygon),
        times,
        suctionMode,
        rotationDeg: longestEdgeAngleDeg(vertices),
      };
    })
    .filter(isNonEmpty);

/**
 * Non-interactive map features (start points, special zones, no-go/glass lines) from `work`/
 * `function`. Empty unless the map is georeferenced (config Resolution + known image size).
 */
export const parseMapFeatures = (
  work: string | null | undefined,
  functionJson: string | null | undefined,
  imageWidthPx?: number | null,
  imageHeightPx?: number | null,
  mapConfig?: string | null
): MapFeatures => {
  const georeference = parseMapConfig(mapConfig);
  if (!georeference || !imageWidthPx || !imageHeightPx) {
    return EMPTY_MAP_FEATURES;
  }

  const toPercent = createMmToPercentConverter({
    bounds: { minX: 0, maxX: 1, minY: 0, maxY: 1 },
    imageWidthPx,
    imageHeightPx,
    georeference,
  });

  const parsedWork = safeParse<RawMapWork>(work);
  const parsedFunction = safeParse<RawMapFunction>(functionJson);

  return {
    startPoints: parseStartPoints(parsedWork?.GoalWithHeading ?? [], toPercent),
    specialZones: parseSpecialZones(parsedWork?.SweepArea ?? [], toPercent),
    noGoAreas: (parsedFunction?.ForbiddenArea ?? []).map(box => toRect(box, toPercent)).filter(isNonEmpty),
    noGoLines: (parsedFunction?.ForbiddenLine ?? []).map(box => toLine(box, toPercent)).filter(isNonEmpty),
    glassLines: (parsedFunction?.GlassLine ?? []).map(box => toLine(box, toPercent)).filter(isNonEmpty),
  };
};
